import React, { Fragment, useEffect, useState } from "react";
import * as Ariakit from "@ariakit/react";
import { XCircle, AlertTriangle } from "lucide-react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import { Color } from "../../constants/color";
import ResetCss from "../ResetCss";

import "./dashboardpage.css";
import { BE_BASE_URL } from "../../utils/config";
import { DashboardSummary, DipendentiDashboardData } from "../../types";
import { axiosInstance, useAuth } from "../../provider/authProvider";
import { fetchMappedFabbisognoData } from "../../pages/training-needs";
import { getDashboardSummary } from "../../features/dashboard/utils";
import { getDashboardEmployees } from "../../features/employee/utils";
import { raggruppaPerSede } from "../../features/employee/sedi";
import { RIEPILOGO_VUOTO } from "../../features/dashboard/riepilogo";
import KpiAttestati from "../kpi-attestati";
import { Loader } from "../../components/loader";
import Button from "../../components/button/button";

const Layout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  row-gap: 2rem;
  column-gap: 1rem;
  justify-content: center;
  padding: 0.5em 1em;
`;

const Card = styled.div`
  border-radius: 8px;
  border: 2px solid var(--blue300);
`;

const Title = styled.h1`
  color: var(--blue300);
  font-size: 40px;
  font-family: "Avenir Next Bold", sans-serif;
  font-weight: bold;
`;

const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${Color.WHITE};
  background-color: ${Color.BLUE};
  border: 0;
  padding: 0.3em 1em;
  border-radius: 8px;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2em;
  padding: 0.2em 1.2em;
`;

const RowLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  grid-gap: 0.6em;
  padding: 0.5em 1em;
  text-align: center;
  align-items: center;
`;

const THead = styled(RowLayout)`
  font-weight: bold;
  text-transform: uppercase;
  color: var(--blue300);
`;

const TRow = styled(RowLayout)`
  color: var(--blue500);
  font-weight: bold;
  text-transform: uppercase;
  text-wrap: balance;
  border: 1px solid var(--grey800);
  border-radius: 8px;
  background-color: var(--grey100);
`;

type FabbisognoDataType = {
  inclusi: {
    id: number;
    title: string;
    fabbisogno: number;
    attivi: number;
    dafare: number;
    subscribed: boolean;
    associatedCourseTitle: string;
  }[];
  esclusi: {
    title: string;
  }[];
};

export default function DashboardPage() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedExpire, setSelectedExpire] = useState(1);
  const [employeeLimitWarning, setEmployeeLimitWarning] = useState<
    string | null
  >(null);
  const [riepilogo, setRiepilogo] = useState<DashboardSummary>(RIEPILOGO_VUOTO);

  const [employees, setEmployees] = useState<DipendentiDashboardData | null>(
    null,
  );
  const [fabbisognoData, setFabbisognoData] = useState<FabbisognoDataType>({
    inclusi: [],
    esclusi: [],
  });
  const [loading, setLoading] = useState({
    show: true,
    message: "Caricamento in corso...",
  });

  useEffect(() => {
    const fetchData = async () => {
      const mappedData = await fetchMappedFabbisognoData(() => {});
      setFabbisognoData(mappedData); // Check if employee limit is close to being reached
      try {
        const responseLimitClose = await axiosInstance.get(
          `${BE_BASE_URL}/employee/limit-close`,
        );
        if (responseLimitClose.status === 200 && responseLimitClose.data) {
          setEmployeeLimitWarning(responseLimitClose.data);
        } else {
          setEmployeeLimitWarning(null);
        }
      } catch {
        setEmployeeLimitWarning(null);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading({ show: true, message: "Caricamento in corso..." });
      // I KPI arrivano in una sola richiesta dal riepilogo del backend.
      const [data, summary] = await Promise.all([
        getDashboardEmployees(),
        getDashboardSummary(),
      ]);
      /*
       Il raggruppamento per sede di lavoro sta in `raggruppaPerSede`: sede e mansione sono
       testo libero che puo' mancare, e la normalizzazione e' la stessa usata dai filtri.
      */
      const dipendentiData = raggruppaPerSede(data, {
        sedeNonIndicata: t("dashboard-sede-non-indicata"),
        mansioneNonIndicata: t("dashboard-mansione-non-indicata"),
      });

      setEmployees(dipendentiData);
      setRiepilogo(summary);
      setLoading({ show: false, message: "" });
    };

    fetchData();
  }, [token, t]);

  const nonSubscribedCourses = fabbisognoData.inclusi
    .filter((row) => !row.subscribed)
    .map((row) => row.associatedCourseTitle);

  return (
    <>
      <Loader show={loading.show}>{loading.message}</Loader>
      <ResetCss>
        <div
          style={{
            margin: "0 auto",
            width: "1200px",
          }}
        >
          <Layout>
            <div
              style={{
                gridColumn: "1/4",
                alignContent: "center",
                backgroundColor: "var(--gray500)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Title>{t("dashboard-title")}</Title>
            </div>
            <Card
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <KpiAttestati
                riepilogo={riepilogo}
                mesi={selectedExpire}
                onMesiChange={setSelectedExpire}
              />
            </Card>
            <Card>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "1rem",
                  padding: "1rem 1rem",
                  height: "100%",
                  width: "100%",
                }}
              >
                <p
                  style={{
                    fontSize: "2rem",
                    color: Color.LIGHT_BLUE,
                    fontWeight: "bolder",
                    lineHeight: "1em",
                  }}
                >
                  {t("dashboard-training-needs")}
                </p>
                <OutlineButton onClick={() => setOpen(true)}>
                  Visualizza
                </OutlineButton>
              </div>
            </Card>{" "}
            <Card style={{ gridRow: "3/4", gridColumn: "1/3" }}>
              <DipendentiCard employees={employees} />
            </Card>
            {/* NEWS SECTION */}
            <Card style={{ gridRow: "4/5", gridColumn: "1/3" }}>
              <div
                style={{
                  padding: "1rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div style={{ flex: "1" }}>
                  <p
                    style={{
                      fontSize: "2rem",
                      color: "var(--blue300)",
                      fontWeight: "bolder",
                      lineHeight: "1em",
                      marginBottom: "1rem",
                    }}
                  >
                    {t("dashboard-news")}
                  </p>{" "}
                  {employeeLimitWarning &&
                    employeeLimitWarning.trim() !== "" && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          backgroundColor: "rgba(255, 204, 0, 0.2)",
                          padding: "1rem",
                          borderRadius: "8px",
                          border: "1px solid #FFCC00",
                        }}
                      >
                        <AlertTriangle size={24} color={Color.ORANGE} />
                        <p
                          style={{
                            color: "var(--blue500)",
                            fontWeight: "bold",
                          }}
                        >
                          {employeeLimitWarning}
                        </p>
                      </div>
                    )}
                </div>
              </div>
            </Card>
          </Layout>
        </div>

        <Ariakit.Dialog
          open={open}
          onClose={() => setOpen(false)}
          className="dialog"
        >
          {/*
           Il livello 1 e' del titolo della pagina, e ce n'e' uno solo: senza questo
           vincolo la Dashboard ne aveva sei, e per un lettore di schermo sembrava sei
           documenti sovrapposti.
          */}
          <Ariakit.DialogHeading className="heading" render={<h2 />}>
            Fabbisogno formativo calcolato
            <Loader show={loading.show}>{loading.message}</Loader>
            <Ariakit.DialogDismiss
              aria-label="Chiudi"
              style={{
                position: "absolute",
                backgroundColor: "transparent",
                border: "none",
                top: ".5em",
                right: ".5em",
                cursor: "pointer",
              }}
            >
              <XCircle size={24} color={Color.GREEN} />
            </Ariakit.DialogDismiss>
          </Ariakit.DialogHeading>
          <DialogContent>
            {/* TITOLI */}
            <THead>
              <p></p>
              <p>Fabbisogno formativo</p>
              <p>Svolti</p>
              <p>Da fare</p>
              <p></p>
            </THead>
            {/* ROWS TABLE */}
            {fabbisognoData.inclusi.map((row) => (
              <TRow
                key={row.id}
                style={{
                  backgroundColor: row.subscribed
                    ? "var(--grey100)"
                    : "lightcoral",
                }} // Colore di sfondo
              >
                <p style={{ textAlign: "left" }}>{row.associatedCourseTitle}</p>
                <p>{row.fabbisogno}</p>
                <p>{row.attivi}</p>
                <p>{row.dafare}</p>
                <a
                  key={row.id}
                  href={(() => {
                    const params = new URLSearchParams();
                    params.append("corso", row.associatedCourseTitle);
                    return `/attestati?${params.toString()}`;
                  })()}
                >
                  <Button
                    style={{ marginTop: 0 }}
                    label="DETTAGLIO"
                    color={Color.BLUE}
                  />
                </a>
              </TRow>
            ))}
            {nonSubscribedCourses.length > 0 && (
              <p
                style={{
                  margin: "1rem",
                  color: "red",
                  textAlign: "start",
                  whiteSpace: "pre-wrap",
                }}
              >
                *I corsi evidenziati in rosso non fanno parte del pacchetto di
                abbonamento acquistato.
              </p>
            )}
          </DialogContent>
        </Ariakit.Dialog>
      </ResetCss>
    </>
  );
}

const DipLayout = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  gap: 2rem;
`;

const DipTable = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 2fr;
  font-size: 1.2rem;
  column-gap: 2rem;
  row-gap: 2rem;
`;

const DipTitle = styled.div`
  color: var(--blue300);
  font-size: 2rem;
  font-weight: bolder;
  line-height: 1em;
`;

const DipTableTitle = styled.p`
  padding: 0.2rem 0;
  font-weight: bold;
`;

const DipTableList = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  align-items: center;
  justify-content: space-between;
  gap: 0.2rem;
  padding: 0.2rem 1rem;
  & p[x-data="title"] {
    font-weight: bold;
  }
  & p[x-data="number"] {
    font-variant-numeric: lining-nums;
    text-align: right;
  }
`;

function DipendentiCard({
  employees,
}: {
  employees: DipendentiDashboardData | null;
}) {
  let rowCounter = 0;

  return (
    <DipLayout>
      <DipTable>
        {/* Prima riga titolo piu totale e neoassunti */}
        <DipTitle>Dipendenti</DipTitle>
        <DipTableList
          style={{
            gridColumn: "2/5",
          }}
        >
          <div
            style={{
              gridRow: "1",
              gridColumn: "1/-1",
              borderBottom: "2px dashed var(--grey800)",
              height: "100%",
            }}
          />
          <p
            style={{
              gridRow: "1",
              gridColumn: "1",
            }}
            x-data="title"
          >
            Numero totale
          </p>
          <p
            style={{
              gridRow: "1",
              gridColumn: "2",
            }}
            x-data="number"
          >
            {employees?.tot}
          </p>
        </DipTableList>
        <p></p>
        {/* Seconda riga */}
        {/* Elenco dipendenti per sede */}
        <DipTableTitle
          style={{
            gridColumn: "1",
            gridRow: "2",
            textAlign: "right",
          }}
        >
          SEDE
        </DipTableTitle>
        <DipTableList
          style={{
            gridColumn: "2/5",
            gridRow: "2",
          }}
        >
          <div
            style={{
              display: "grid",
              gridColumn: "1/3",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "0.2rem",
            }}
          >
            {employees?.sedi.map((sede) => {
              rowCounter++;
              return (
                <Fragment key={sede.nome}>
                  <div
                    style={{
                      gridRow: `${rowCounter}`,
                      gridColumn: "1/-1",
                      borderBottom: "2px dashed var(--grey800)",
                    }}
                  />
                  <p
                    x-data="title"
                    style={{
                      gridRow: `${rowCounter}`,
                      gridColumn: "1/2",
                    }}
                  >
                    {sede.nome}
                  </p>
                  <p
                    x-data="number"
                    style={{
                      gridRow: `${rowCounter}`,
                      gridColumn: "3",
                      textAlign: "right",
                    }}
                  >
                    {sede.dipendenti}
                  </p>
                  {Array.from(sede.mansioni.values()).map((mansione) => {
                    rowCounter++;
                    return (
                      <Fragment key={mansione.nome}>
                        <p
                          style={{
                            gridRow: `${rowCounter}`,
                            gridColumn: "2/-1",
                            borderBottom: "2px dashed var(--grey500)",
                          }}
                        />
                        <p
                          x-data="title"
                          style={{
                            gridRow: `${rowCounter}`,
                            gridColumn: "2",
                            color: "var(--grey800)",
                          }}
                        >
                          {mansione.nome}
                        </p>
                        <p
                          x-data="number"
                          style={{
                            gridRow: `${rowCounter}`,
                            gridColumn: "3",
                            textAlign: "right",
                            color: "var(--grey800)",
                          }}
                        >
                          {mansione.dipendenti}
                        </p>
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
          </div>
        </DipTableList>
      </DipTable>
    </DipLayout>
  );
}
